import { FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useAppDispatch } from '@/store';
import { loginUser, loginWithGoogle } from '@/store/slices/authSlice';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { useRedirectTarget } from '@/hooks/useRedirectTarget';

export function LoginPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { status, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [googleError, setGoogleError] = useState<string | null>(null);

  const redirectTo = useRedirectTarget();
  // Carry the same `from` state through to /register if the user switches tabs.
  const from = (location.state as { from?: unknown } | null)?.from;

  const isLoading = status === 'loading';

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setGoogleError(null);
    const result = await dispatch(loginUser({ email, password }));
    if (loginUser.fulfilled.match(result)) {
      navigate(redirectTo, { replace: true });
    }
  };

  const handleGoogle = async (credential: string): Promise<void> => {
    setGoogleError(null);
    const result = await dispatch(loginWithGoogle(credential));
    if (loginWithGoogle.fulfilled.match(result)) {
      navigate(redirectTo, { replace: true });
    }
  };

  // Auth error from the slice (login/google) OR a local Google-sdk failure.
  const shownError = googleError ?? error;

  return (
    <main className="auth-page">
      <div className="auth-card">
        <h1>{t('auth.login.title')}</h1>
        <p className="muted">{t('auth.login.blurb')}</p>

        {shownError && (
          <div className="error" role="alert">
            {shownError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="btn" disabled={isLoading}>
            {isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
          </button>
        </form>

        <div className="divider">{t('auth.or')}</div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <GoogleSignInButton
            onCredential={handleGoogle}
            onError={() => setGoogleError(t('auth.googleError'))}
          />
        </div>

        <div className="footer-link">
          <Trans
            i18nKey="auth.login.footer"
            components={{ link: <Link to="/register" state={from ? { from } : undefined} /> }}
          />
        </div>
      </div>
    </main>
  );
}
